import * as React from "react";
import { Input, InputProps } from "./Input";
import { Label } from "./label";
import { RefCallback, RefObject } from "react";
import { cva, VariantProps } from "class-variance-authority";
import { cn } from "@/shared/utils";
import { ErrorMessage } from "../FormField";

const inputLabelVariants = cva("relative flex", {
    variants: {
        variant: {
            default: "",
            floating: "items-center rounded-md border border-none bg-background-primary shadow-faux-border",
            "floating-inner": "rounded-xl"
        }
    },
    defaultVariants: {
        variant: "default"
    }
});

interface InputWithLabelProps extends VariantProps<typeof inputLabelVariants> {
    id?: string;
    label: string;
    inputProps?: InputProps;
    inputRef?: RefObject<HTMLInputElement> | RefCallback<HTMLInputElement>;
    startDecorator?: React.ReactNode;
    endDecorator?: React.ReactNode;
    wrapperClass?: string;
    error?: ErrorMessage;
    inputTestId?: string;
}

const InputWithLabel = (props: InputWithLabelProps) => {
    const { id, label, wrapperClass, inputProps, inputRef, startDecorator, endDecorator, variant, error, inputTestId } = props;
    const inputId = id ?? label?.split(" ").join("-").toLowerCase();
    const inputHasValue = String(inputProps?.value ?? "").length > 0;
    const inputClass = `input-value-${inputHasValue}`;
    const isDefaultVariant = variant === "default" || variant === undefined;
    const isFloatingVariant = variant?.includes("floating");
    const placeholder = inputProps?.placeholder ?? " ";

    const labelClassConditions = {
        "left-10": startDecorator && !inputHasValue,
        "absolute -top-6 text-xs": isDefaultVariant,
        "absolute top-2.5 peer-placeholder-shown:text-center peer-placeholder-shown:text-base top-3 text-xs": isFloatingVariant
    };

    const decoratorClassConditions = {
        "absolute bottom-3 left-3 pl-0 z-50": isDefaultVariant,
        "absolute left-3 z-50": isFloatingVariant
    };

    const inputClassConditions = {
        "pl-12": isDefaultVariant && !!startDecorator,
        "pl-11": isFloatingVariant && !!startDecorator
    };

    return (
        <div className={cn(inputLabelVariants({ variant }), wrapperClass)} data-testid="input-wrapper">
            {startDecorator && <span className={cn("flex items-center", decoratorClassConditions)}>{startDecorator}</span>}
            <Input
                id={inputId}
                variant={variant}
                className={cn(inputClass, inputClassConditions)}
                ref={inputRef}
                placeholder={placeholder}
                error={error}
                inputTestId={inputTestId}
                {...inputProps}
            />
            <Label
                htmlFor={inputId}
                variant={variant}
                bgColor={isDefaultVariant ? "transparent" : "default"}
                className={cn("absolute left-2 transition-all duration-150 peer-focus:text-xs", labelClassConditions)}
                data-testid="label"
            >
                {label}
            </Label>
            {endDecorator && <span className={cn("absolute bottom-3 right-4 z-50 flex items-center")}>{endDecorator}</span>}
        </div>
    );
};

InputWithLabel.displayName = "InputWithLabel";

export { InputWithLabel };
